import { createClient } from '@supabase/supabase-js';

// Environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabaseSchema = import.meta.env.VITE_SUPABASE_SCHEMA || 'public';

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    '❌ Missing Supabase environment variables! Please check your .env file for VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.'
  );
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  db: {
    schema: supabaseSchema,
  },
  realtime: {
    // Configure realtime settings
    params: {
      eventsPerSecond: 10, // Limit events per second to prevent overwhelming
    },
  },
  auth: {
    // Use browser's built-in sessionStorage for healthcare applications
    // This provides the optimal balance of security and user experience:
    // - Sessions survive browser refresh (excellent UX for OTP auth)
    // - Sessions cleared when tab closes (good security)
    // - Battle-tested by Supabase team (robust and reliable)
    storage: typeof window !== 'undefined' ? window.sessionStorage : undefined,
    // Auto refresh tokens for seamless experience
    autoRefreshToken: true,
    // Persist session to sessionStorage (not localStorage)
    persistSession: true,
    // Detect session in URL (for OAuth flows)
    detectSessionInUrl: true,
    // PKCE flow for enhanced security
    flowType: 'pkce',
  },
});

// Database types - matches your actual table structure
export interface UserProfile {
  id: string;
  auth_user_id?: string;
  ev_integration_partner_id?: string;
  username?: string;
  email?: string;
  phone: string;
  first_name: string;
  last_name: string;
  gender?: string;
  avatar_url?: string;
  is_active: boolean;
  last_login?: string;
  created_at: string;
  updated_at: string;
}

// Integration Partner types
export interface IntegrationPartner {
  id: string;
  name: string;
  contact_email?: string;
  contact_phone?: string;
  cloud_secret_id?: string;
  note?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Integration Endpoint types
export interface IntegrationEndpoint {
  id: string;
  action: string;
  description: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  created_at: string;
  updated_at: string;
}

// Integration Partner Endpoint types
export interface IntegrationPartnerEndpoint {
  id: string;
  ev_integration_partner_id: string;
  ev_integration_endpoint_id: string;
  is_active: boolean;
  environment: 'development' | 'staging' | 'production';
  endpoint_url: string;
  created_at: string;
  updated_at: string;
  // Joined data
  integration_endpoint?: IntegrationEndpoint;
}

// Export types for use throughout the app
export type User = UserProfile;
